<template>
  <div class="dca-matrix gap-1" data-testid="dca-allocation-matrix">
    <div class="dca-matrix__head" />
    <template v-for="step in placementNumbers" :key="`head-${step}`">
      <div
        class="dca-matrix__head text-caption text-medium-emphasis text-center"
      >
        {{ columnLabel(step) }}
      </div>
      <div v-if="hasGateColumn(step)" class="dca-matrix__head" />
    </template>

    <template v-for="row in segmentRows" :key="row.id">
      <div class="dca-matrix__label">
        <div class="text-body-2 font-weight-medium">{{ row.label }}</div>
        <SoTooltip
          text="PSR is not configured separately — this split governs PSR and non-PSR accounts alike."
        >
          <span class="text-caption text-medium-emphasis">PSR and non-PSR</span>
        </SoTooltip>
      </div>

      <template v-for="step in placementNumbers" :key="`${row.id}-${step}`">
        <DcaAllocationCell
          :row-id="row.id"
          :placement-number="step"
          :allocations="allocationsFor(row, step)"
          :selected="isSelected(row, step)"
          :loading="loading"
          @select="$emit('select', { row, placementNumber: step })"
        />
        <template v-if="hasGateColumn(step)">
          <DcaMoriartyGate v-if="row.gateAfterStep === step" :row-id="row.id" />
          <div v-else />
        </template>
      </template>
    </template>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import DcaAllocationCell from "./DcaAllocationCell.vue"
  import DcaMoriartyGate from "./DcaMoriartyGate.vue"
  import {
    PLACEMENT_NUMBERS,
    ISegmentRow,
    SEGMENT_ROWS,
    placementLabel,
  } from "../dcaAllocation.config"
  import {
    IDcaAllocation,
    IDcaAllocationCell,
  } from "@/models/dcaAllocation/dcaAllocation.types"
  import { findCell } from "@/services/DcaAllocationService"

  export interface ISelection {
    row: ISegmentRow
    placementNumber: number
  }

  export default defineComponent({
    components: { DcaAllocationCell, DcaMoriartyGate, SoTooltip },
    props: {
      cells: {
        type: Array as PropType<IDcaAllocationCell[]>,
        default: () => [],
      },
      selection: {
        type: Object as PropType<ISelection | null>,
        default: null,
      },
      loading: { type: Boolean, default: false },
    },
    emits: ["select"],
    data() {
      return {
        segmentRows: SEGMENT_ROWS,
        placementNumbers: PLACEMENT_NUMBERS,
      }
    },
    methods: {
      /** Column heads name what the column is: "1st DCA placement". */
      columnLabel(placementNumber: number): string {
        return placementLabel(placementNumber).replace(
          "placement",
          "DCA placement",
        )
      },
      /**
       * A gate column follows every placement that some segment refers after -
       * Known Live after its 2nd, Known Final after its 1st. The column exists on
       * every row so the grid stays aligned; rows without a gate there render an
       * empty slot. There is no gate after the last placement.
       */
      hasGateColumn(step: number): boolean {
        return this.segmentRows.some((row) => row.gateAfterStep === step)
      },
      allocationsFor(row: ISegmentRow, step: number): IDcaAllocation[] {
        return (
          findCell(this.cells, row.occupier, row.accountState, step)
            ?.allocations ?? []
        )
      },
      isSelected(row: ISegmentRow, step: number): boolean {
        return (
          this.selection?.row.id === row.id &&
          this.selection?.placementNumber === step
        )
      },
    },
  })
</script>

<style scoped>
  .dca-matrix {
    display: grid;
    /*
     * Label, then each placement followed by its gate track. The gate tracks are
     * narrow on purpose - Moriarty is a marker between placements, not a column
     * of its own, and it should not compete with the cells for width.
     *
     * Six tracks for three placements and the two gates is deliberate, as is the
     * four fixed segment rows and Moriarty's fixed position between them. It is
     * not a config-driven grid and is not meant to become one: a fourth placement
     * needs the gateway's `placement in 1..4` guard changed and a fifth needs a
     * migration, so extending the matrix is development work either way. Keeping
     * the shape literal here means it reads as the decision it is rather than as
     * a limitation someone should generalise away.
     */
    grid-template-columns:
      140px minmax(120px, 1fr) 26px minmax(120px, 1fr)
      26px minmax(120px, 1fr);
    align-items: stretch;
  }

  .dca-matrix__head {
    padding-bottom: 4px;
  }

  .dca-matrix__label {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-right: 8px;
  }
</style>
